import React, { useEffect, useState } from 'react';

import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { Rule } from 'common/filters/filter-bar/AdvancedFilter/Rule';
import { Operator, TextType } from 'common/filters/Filters.types';

import 'common/filters/filter-bar/AdvancedFilter/AdvancedFilter.scss';

interface AdvancedFilterProps {
    show: boolean;
    onHide: () => void;
    onDelete?: (filterId: string) => void;
    onSave: (filterData: any) => void;
    initialShortName?: string;
    initialRulesData?: {
        id: string;
        rules: {
            query: {
                field: string;
                fieldKey: string;
                operator: number;
                valueString: string[];
            }[];
        }[];
    } | null;
}

interface RuleGroupType {
    id: string;
    rules: RuleType[];
}

type FieldType = string;
interface RuleType {
    id: string;
    field: FieldType;
    operator: number;
    value: string; // comma-separated values
}

export const AdvancedFilter: React.FC<AdvancedFilterProps> = ({
    show,
    onHide,
    onDelete,
    onSave,
    initialShortName = '',
    initialRulesData,
}) => {
    // State: array of rule groups
    const [shortName, setShortName] = useState<string>(initialShortName);
    // Now ruleGroups is an array of { id, rules }
    const [ruleGroups, setRuleGroups] = useState<RuleGroupType[]>([]);
    const [canShowDetails, setCanShowDetails] = useState<boolean>(show);
    const [canSave, setCanSave] = useState<boolean>(false);
    const [canDelete, setCanDelete] = useState<boolean>(false);
    const { t } = useTranslation(['common/filters']);

    // Populate shortName and ruleGroups when opening
    useEffect(() => {
        if (show) {
            setShortName(initialShortName);

            if (initialRulesData) {
                const groups: RuleGroupType[] = initialRulesData.rules?.map((grp) => ({
                    id: makeId(),
                    rules: grp.query.map((q) => ({
                        id: makeId(),
                        field: TextType.MentionText,
                        operator: q.operator,
                        value: q.valueString.join(', '),
                    })),
                }));

                setRuleGroups(groups);
            } else {
                // Start with one group containing one empty rule
                setRuleGroups([
                    {
                        id: makeId(),
                        rules: [
                            {
                                id: makeId(),
                                field: TextType.MentionText,
                                operator: Operator.ContainsWord,
                                value: '',
                            },
                        ],
                    },
                ]);
            }
        }
    }, [show, initialShortName, initialRulesData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Validation helper for save
    const isRuleValid = (value: string): boolean => {
        const keywords = value
            .split(',')
            .map((kw) => kw.trim())
            .filter((kw) => kw !== '');

        if (keywords.length > 20) return false;
        const allAlphanumeric = keywords.every((kw) => /^[a-zA-Z0-9 #&@*!+-_]+$/.test(kw));

        if (!allAlphanumeric) return false;
        return true;
    };

    // Enable Save: needs shortName, at least one non-empty & valid value, and no invalid values
    useEffect(() => {
        // “hasValue” is true if any rule in any group has a non‐empty value
        const hasValue = ruleGroups?.some((group) => group.rules.some((r) => r.value.trim() !== ''));

        // “allValid” is true if, for every group, every non‐empty rule value passes isRuleValid
        const allValid = ruleGroups?.every((group) =>
            group.rules.every((r) => r.value.trim() === '' || isRuleValid(r.value)),
        );

        setCanSave(shortName.trim() !== '' && hasValue && allValid);
    }, [shortName, ruleGroups]);

    const addRuleGroup = (): void => {
        setRuleGroups((prev) => [
            ...prev,
            {
                id: makeId(),
                rules: [{ id: makeId(), field: TextType.MentionText, operator: Operator.ContainsWord, value: '' }],
            },
        ]);
    };

    const removeRuleGroup = (groupId: string): void => {
        setRuleGroups((prev) => prev.filter((g) => g.id !== groupId));
    };

    const addRule = (groupId: string): void => {
        setRuleGroups((prev) =>
            prev.map((g) =>
                g.id === groupId
                    ? {
                          ...g,
                          rules: [
                              ...g.rules,
                              { id: makeId(), field: TextType.MentionText, operator: Operator.ContainsWord, value: '' },
                          ],
                      }
                    : g,
            ),
        );
    };

    const removeRule = (groupId: string, ruleIndex: number): void => {
        setRuleGroups(
            (prev) =>
                prev
                    .map((g) =>
                        g.id === groupId
                            ? {
                                  ...g,
                                  rules: g.rules.filter((_, idx) => idx !== ruleIndex),
                              }
                            : g,
                    )
                    .filter((g) => g.rules.length > 0), // if a group ends up empty, drop it entirely
        );
    };

    const updateRule = (groupId: string, ruleIndex: number, key: keyof Omit<RuleType, 'id'>, value: string): void => {
        setRuleGroups((prev) =>
            prev.map((g) =>
                g.id === groupId
                    ? {
                          ...g,
                          rules: g.rules.map((r, idx) => (idx === ruleIndex ? { ...r, [key]: value } : r)),
                      }
                    : g,
            ),
        );
    };

    const handleSave = (): void => {
        const payload: any = {
            name: shortName.trim(),
            rules: ruleGroups.map((group) => ({
                query: group.rules.map((rule) => ({
                    field: 'raw_body_text',
                    fieldKey: 'raw_body_text',
                    operator: rule.operator,
                    valueString: rule.value
                        .split(',')
                        .map((v) => v.trim())
                        .filter((v) => v !== ''),
                })),
            })),
        };

        if (initialRulesData?.id) {
            payload.id = initialRulesData?.id;
        }

        onSave(payload);
        console.log(JSON.stringify(payload, null, 2));
    };

    const handleDelete = (): void => {
        setCanShowDetails(false);
        setCanDelete(true);
    };

    const confirmDelete = (): void => {
        setCanDelete(false);
        if (initialRulesData?.id && onDelete) {
            onDelete(initialRulesData.id);
        }
    };

    const onHideDelete = (): void => {
        setCanShowDetails(true);
        setCanDelete(false);
    };

    const makeId = () => {
        return Date.now().toString(36) + Math.random().toString(36).slice(2);
    };

    return (
        <>
            <Modal show={canShowDetails} onHide={onHide} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {initialShortName ? `${t('EDIT')} ${initialShortName} ${t('FILTER')}` : t('ADD_FILTER')}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>{t('SHORT_NAME')}</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={t('SHORT_NAME_PLACEHOLDER')}
                            value={shortName}
                            onChange={(e) => setShortName(e.target.value)}
                        />
                    </Form.Group>

                    <div className="mt-4">
                        {ruleGroups?.map((group) => (
                            <div key={group.id} className="mb-4 p-3 border rounded">
                                {ruleGroups.indexOf(group) > 0 && <strong>OR</strong>}
                                {group.rules.map((rule, ri) => (
                                    <Rule
                                        key={rule.id}
                                        rule={rule}
                                        groupId={group.id}
                                        ruleIndex={ri}
                                        onRuleChange={updateRule}
                                        onRemove={removeRule}
                                        showRemoveButton={group.rules.length > 1}
                                    />
                                ))}
                                <Button variant="link" onClick={() => addRule(group.id)}>
                                    + {t('ADD_RULE')}
                                </Button>
                                {ruleGroups.length > 1 && (
                                    <Button
                                        variant="link"
                                        className="text-danger ml-3"
                                        onClick={() => removeRuleGroup(group.id)}
                                    >
                                        {t('REMOVE_GROUP')}
                                    </Button>
                                )}
                            </div>
                        ))}
                        <div className="mb-4 p-3 rules-group-block rounded">
                            <Button variant="link" onClick={addRuleGroup}>
                                + {t('ADD_RULE_GROUP')}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    {onDelete && initialRulesData && (
                        <Button variant="link" className="text-danger" onClick={handleDelete}>
                            {t('DELETE')}
                        </Button>
                    )}
                    <Button variant="secondary" onClick={onHide}>
                        {t('CANCEL')}
                    </Button>
                    <Button variant="primary" onClick={handleSave} disabled={!canSave}>
                        {t('SAVE')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <>
                <Modal show={canDelete} onHide={onHideDelete} size="lg" centered>
                    <Modal.Header closeButton>
                        <Modal.Title> {t('DELETE_HEADER', { filterName: initialShortName })} </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div> {t('DELETE_CONTENT')} </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={onHideDelete}>
                            {t('CANCEL')}
                        </Button>
                        <Button variant="primary" onClick={confirmDelete}>
                            {t('REMOVE_FILTER_BUTTON')}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </>
        </>
    );
};
